import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  { value: '#1E3A5F', label: 'Navy (DealLab)' },
  { value: '#0EA5E9', label: 'Sky Blue' },
  { value: '#0284C7', label: 'Ocean Blue' },
  { value: '#059669', label: 'Green' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#DC2626', label: 'Red' },
];

export default function CourseEditorPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isNew = !courseId;

  const [course, setCourse] = useState({
    title: '',
    description: '',
    credit_hours: 1,
    category: 'Real Estate Finance',
    delivery_format: 'both',
    color: '#1E3A5F'
  });
  const [slides, setSlides] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchCourse();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}`);
      const data = response.data;
      setCourse({
        title: data.title,
        description: data.description,
        credit_hours: data.credit_hours,
        category: data.category,
        delivery_format: data.delivery_format,
        color: data.color
      });
      setSlides(data.slides || []);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    if (!course.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const response = await axios.post(`${API_URL}/courses`, course);
        toast.success('Course created!');
        navigate(`/edit/${response.data.id}`);
      } else {
        await axios.put(`${API_URL}/courses/${courseId}`, course);
        toast.success('Course updated!');
      }
    } catch (error) {
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSlide = async () => {
    if (isNew) {
      toast.error('Save the course first before adding slides');
      return;
    }

    const newSlide = {
      title: `Slide ${slides.length + 1}`,
      content: 'Enter your content here...',
      slide_type: 'content',
      notes: ''
    };

    try {
      const response = await axios.post(`${API_URL}/courses/${courseId}/slides`, newSlide);
      setSlides([...slides, response.data]);
      setSelectedSlide(response.data);
      toast.success('Slide added');
    } catch (error) {
      toast.error('Failed to add slide');
    }
  };

  const handleUpdateSlide = async (slideData) => {
    if (!selectedSlide) return;

    try {
      await axios.put(`${API_URL}/courses/${courseId}/slides/${selectedSlide.id}`, slideData);
      setSlides(slides.map(s => s.id === selectedSlide.id ? { ...s, ...slideData } : s));
      setSelectedSlide({ ...selectedSlide, ...slideData });
      toast.success('Slide updated');
    } catch (error) {
      toast.error('Failed to update slide');
    }
  };

  const handleDeleteSlide = async (slideId) => {
    if (!window.confirm('Delete this slide?')) return;

    try {
      await axios.delete(`${API_URL}/courses/${courseId}/slides/${slideId}`);
      setSlides(slides.filter(s => s.id !== slideId));
      if (selectedSlide?.id === slideId) {
        setSelectedSlide(null);
      }
      toast.success('Slide deleted');
    } catch (error) {
      toast.error('Failed to delete slide');
    }
  };

  const moveSlide = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= slides.length) return;

    const newSlides = [...slides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
    setSlides(newSlides);

    // Save reorder to backend
    const slideOrder = newSlides.map(s => s.id);
    axios.put(`${API_URL}/courses/${courseId}/slides/reorder`, slideOrder).catch(() => {
      toast.error('Failed to reorder');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 animate-pulse">Loading course...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <img 
              src="/images/deallab-logo.png" 
              alt="DealLab" 
              className="h-8 w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <Link to={`/present/${courseId}`}>
                <Button variant="outline" className="border-sky-500 text-sky-600 hover:bg-sky-50">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </Link>
            )}
            <Button 
              onClick={handleSaveCourse} 
              disabled={saving}
              className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Details */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    placeholder="Course Title"
                    data-testid="course-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={course.description}
                    onChange={(e) => setCourse({ ...course, description: e.target.value })}
                    placeholder="Course description..."
                    rows={3}
                    data-testid="course-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="credit_hours">CE Hours</Label>
                    <Select
                      value={course.credit_hours.toString()}
                      onValueChange={(v) => setCourse({ ...course, credit_hours: parseInt(v) })}
                    >
                      <SelectTrigger data-testid="ce-hours-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Hour</SelectItem>
                        <SelectItem value="2">2 Hours</SelectItem>
                        <SelectItem value="3">3 Hours</SelectItem>
                        <SelectItem value="4">4 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="format">Format</Label>
                    <Select
                      value={course.delivery_format}
                      onValueChange={(v) => setCourse({ ...course, delivery_format: v })}
                    >
                      <SelectTrigger data-testid="format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="webinar">Webinar</SelectItem>
                        <SelectItem value="in-person">In-Person</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={course.category}
                    onChange={(e) => setCourse({ ...course, category: e.target.value })}
                    placeholder="e.g., Real Estate Finance"
                  />
                </div>

                <div>
                  <Label>Theme Color</Label>
                  <div className="flex gap-2 mt-2">
                    {COLORS.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setCourse({ ...course, color: c.value })}
                        className={`w-8 h-8 rounded-full transition-all ${
                          course.color === c.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                        }`}
                        style={{ backgroundColor: c.value }}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Slides List */}
            {!isNew && (
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Slides ({slides.length})</CardTitle>
                  <Button size="sm" onClick={handleAddSlide} data-testid="add-slide-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {slides.map((slide, idx) => (
                      <div
                        key={slide.id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedSlide?.id === slide.id 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedSlide(slide)}
                        data-testid={`slide-item-${slide.id}`}
                      >
                        <GripVertical className="w-4 h-4 text-slate-300" />
                        <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                        <span className="flex-1 text-sm truncate">{slide.title}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); moveSlide(idx, 'up'); }}
                            disabled={idx === 0}
                          >
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); moveSlide(idx, 'down'); }}
                            disabled={idx === slides.length - 1}
                          >
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={(e) => { e.stopPropagation(); handleDeleteSlide(slide.id); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Slide Editor */}
          <div className="lg:col-span-2">
            {selectedSlide ? (
              <Card className="border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Edit Slide</CardTitle>
                    <Badge variant="outline">{selectedSlide.slide_type}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Slide Title</Label>
                    <Input
                      value={selectedSlide.title}
                      onChange={(e) => setSelectedSlide({ ...selectedSlide, title: e.target.value })}
                      onBlur={() => handleUpdateSlide(selectedSlide)}
                      data-testid="slide-title-input"
                    />
                  </div>

                  <div>
                    <Label>Slide Type</Label>
                    <Select
                      value={selectedSlide.slide_type}
                      onValueChange={(v) => {
                        const updated = { ...selectedSlide, slide_type: v };
                        setSelectedSlide(updated);
                        handleUpdateSlide(updated);
                      }}
                    >
                      <SelectTrigger data-testid="slide-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="title">Title Slide</SelectItem>
                        <SelectItem value="content">Content</SelectItem>
                        <SelectItem value="bullets">Bullet Points</SelectItem>
                        <SelectItem value="section">Section Header</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Content</Label>
                    <Textarea
                      value={selectedSlide.content}
                      onChange={(e) => setSelectedSlide({ ...selectedSlide, content: e.target.value })}
                      onBlur={() => handleUpdateSlide(selectedSlide)}
                      rows={4}
                      data-testid="slide-content-input"
                    />
                  </div>

                  {selectedSlide.slide_type === 'bullets' && (
                    <div>
                      <Label>Bullet Points (one per line)</Label>
                      <Textarea
                        value={(selectedSlide.bullets || []).join('\n')}
                        onChange={(e) => setSelectedSlide({ 
                          ...selectedSlide, 
                          bullets: e.target.value.split('\n').filter(b => b.trim()) 
                        })}
                        onBlur={() => handleUpdateSlide(selectedSlide)}
                        rows={5}
                        placeholder="Enter each bullet point on a new line"
                        data-testid="slide-bullets-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Speaker Notes</Label>
                    <Textarea
                      value={selectedSlide.notes || ''}
                      onChange={(e) => setSelectedSlide({ ...selectedSlide, notes: e.target.value })}
                      onBlur={() => handleUpdateSlide(selectedSlide)}
                      rows={3}
                      placeholder="Notes for yourself during the presentation..."
                      data-testid="slide-notes-input"
                    />
                  </div>

                  {/* Preview */}
                  <div className="mt-6 p-6 rounded-lg bg-slate-100">
                    <p className="text-xs text-slate-500 mb-3">Preview</p>
                    <div 
                      className={`p-6 rounded-lg ${
                        selectedSlide.slide_type === 'title' 
                          ? 'text-white' 
                          : 'bg-white'
                      }`}
                      style={selectedSlide.slide_type === 'title' ? { backgroundColor: course.color } : {}}
                    >
                      <h2 
                        className={`text-2xl font-bold mb-3 ${
                          selectedSlide.slide_type === 'title' ? 'text-white' : 'text-slate-900'
                        }`}
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {selectedSlide.title}
                      </h2>
                      {selectedSlide.slide_type === 'bullets' && selectedSlide.bullets ? (
                        <ul className="space-y-2">
                          {selectedSlide.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-600">
                              <span className="text-sky-500">•</span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={selectedSlide.slide_type === 'title' ? 'text-slate-300' : 'text-slate-600'}>
                          {selectedSlide.content}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="py-24 text-center">
                  <p className="text-slate-500">
                    {isNew 
                      ? 'Save the course first, then add slides' 
                      : 'Select a slide to edit or add a new one'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
